"use client";

import type { FC } from "react";
import { motion } from "motion/react";
import { AppImage } from "@/src/components/NextImage";
import { cn } from "utils";
import { ONBOARDING_WIZARD_ASSETS } from "../onboardingWizardAssets";
import styles from "./loaderEdHero.module.css";

/**
 * Figma H5 `96121:8444` Ping rings: 144 / 296 / 464.
 * Visible sonar stays near the disc (≈144→296); skip Ambient 660/880 (full-bleed on 393).
 * Size is fixed in CSS (`loaderEdHero.module.css`) so keyframes don't rely on custom props.
 */
const FLOAT_CYCLE_MS = 2400;

const SONAR_WAVES = [
  { id: "a", delayMs: 0 },
  { id: "b", delayMs: FLOAT_CYCLE_MS / 2 },
] as const;

interface LoaderEdHeroProps {
  className?: string;
  /** Skip float / sonar (error step or reduced motion harness). */
  staticOnly?: boolean;
}

/**
 * Loader hero — white Ed disc + mark, float ±8px, contact shadow, sonar ripples.
 * H5 `96121:8444` / PC `96344:110581` (`96105:47689` bounce).
 */
export const LoaderEdHero: FC<LoaderEdHeroProps> = ({
  className,
  staticOnly = false,
}) => (
  <div
    className={cn(styles.hero, className)}
    data-testid="loader-ed-hero"
    data-figma-node="96105:47689"
  >
    <div
      className={styles.rippleLayer}
      data-testid="loader-sonar-layer"
      data-figma-node="96105:47683"
    >
      {!staticOnly
        ? SONAR_WAVES.map((wave) => (
            <div
              key={wave.id}
              className={styles.ring}
              style={{
                ["--loader-ripple-delay" as string]: `${wave.delayMs}ms`,
              }}
              data-playing="1"
              data-testid={`loader-sonar-${wave.id}`}
              aria-hidden
            />
          ))
        : null}
    </div>

    <motion.div
      className="relative z-[1] flex flex-col items-center"
      initial={staticOnly ? false : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={
        staticOnly ? { duration: 0 } : { duration: 0.3, ease: "easeOut" }
      }
    >
      <motion.div
        className="relative size-[88px]"
        data-figma-node="96105:47690"
        data-testid="loader-ed-disc"
        animate={
          staticOnly
            ? undefined
            : {
                y: [8, -8, 8],
              }
        }
        transition={
          staticOnly
            ? undefined
            : {
                duration: FLOAT_CYCLE_MS / 1000,
                ease: "easeInOut",
                repeat: Infinity,
              }
        }
      >
        <AppImage
          src={ONBOARDING_WIZARD_ASSETS.loaderEdDisc}
          alt=""
          width={88}
          height={88}
          className="absolute inset-0 size-[88px]"
        />
        <AppImage
          src={ONBOARDING_WIZARD_ASSETS.loaderEdMark}
          alt=""
          width={44}
          height={44}
          className="absolute left-1/2 top-1/2 size-[44px] -translate-x-1/2 -translate-y-1/2"
        />
      </motion.div>

      <motion.div
        className="relative z-0 -mt-1 h-[14px] w-[64px]"
        data-figma-node="96105:47688"
        data-testid="loader-ed-shadow"
        animate={
          staticOnly
            ? undefined
            : {
                scaleX: [1.1, 0.65, 1.1],
                opacity: [0.14, 0.06, 0.14],
              }
        }
        transition={
          staticOnly
            ? undefined
            : {
                duration: FLOAT_CYCLE_MS / 1000,
                ease: "easeInOut",
                repeat: Infinity,
              }
        }
      >
        <AppImage
          src={ONBOARDING_WIZARD_ASSETS.loaderEdShadow}
          alt=""
          width={72}
          height={22}
          className="absolute inset-[-28%_-6%] size-full max-w-none"
        />
      </motion.div>
    </motion.div>
  </div>
);

/** Static 88px Ed mark (error step) — same disc/mark assets, no motion. */
export const EdMarkLarge: FC<{ className?: string }> = ({ className }) => (
  <LoaderEdHero className={className} staticOnly />
);
